import React, { useRef } from "react"
import Styled from "@emotion/styled"
import { fullGrid } from "css/grid"
import { headline50, body50 } from "css/text"
import { gsap } from "gsap/dist/gsap"
import { useGSAP } from "@gsap/react"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import getMediaQuery from "css/breakpoints"
import ImageCld from "components/media/ImageCLD"
import parse from "html-react-parser"
import { useConsole } from "contexts/Console"
import { useLocale } from "contexts/Locale"

gsap.registerPlugin(ScrollTrigger)

export const Section = Styled.section`
  ${fullGrid}
  position: relative;
  text-rendering: optimizeSpeed;
`

const LogoWrapper = Styled.div`
  position: relative;
  grid-column: 4 / span 2;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  max-width: 60px;
  margin: 0 auto;
  margin-block-end: 40px;
  ${getMediaQuery("m")} {
    grid-column: 7 / span 2;
    max-width: 80px;
  }
`

const TextWrapper = Styled.div`
  position: relative;
  grid-column: main;

  ${getMediaQuery("m")} {
    grid-column:  4 / span 8;
  }
  ${getMediaQuery("l")} {
    grid-column:  4 / span 8;
  }
`

const TextBase = Styled.div`
  ${headline50}
  color: rgb(var(--alternate-grey));
  position: relative;
  .prefers-contrast & {
    color: rgb(var(--light-black));
  }
    width: 100%;
    p {
      text-rendering: optimizeSpeed;
      word-break: ${({ isRu }) => (isRu ? "keep-all" : "break-word")};
      word-wrap: break-word;
    }

`

const NoGradientText = Styled(TextBase)`
  position: absolute; 
  top: 0;
  color: rgba(${({ color_gradient }) => {
    const { color, opacity } = color_gradient?.colors[0] || {}
    return `${color} / ${opacity}`
  }});
  z-index: 1;
  width: 100%;

  span {
    opacity: 0;
  }
    p {
    letter-spacing: -0.01px;
  }
  ${getMediaQuery("l", { max: true })} {
     &.retina {
    p {
      letter-spacing: 0;
    }
  }
}

`

const ColoredText = Styled(TextBase)`
  position: absolute; 
  top: 0;
  z-index: 2;
  text-size-adjust: none;
  width: 100%;

  span {
    opacity: 0;
  }
  p {
    letter-spacing: -0.01px;
  }
    ${getMediaQuery("l", { max: true })} {
     &.retina {
    p {
      letter-spacing: 0;
    }
  }
}
  color: rgba(${({ color_gradient }) => {
    const { color, opacity } = color_gradient?.colors[0] || {}
    return `${color} / ${opacity}`
  }});
   ${({ color_gradient }) => {
     const colorLength = color_gradient?.colors.length || 0
     if (colorLength < 2) {
       return ""
     }
     if (colorLength === 2) {
       const color1 = color_gradient?.colors[0] || {}
       const color2 = color_gradient?.colors[1] || {}
       return `
        background-image: linear-gradient(90deg, rgba(${color1.color} / ${color1.opacity}) 0%, rgba(${color2.color} / ${color2.opacity}) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      `
     }
     if (colorLength >= 3) {
       const color1 = color_gradient?.colors[0] || {}
       const color2 = color_gradient?.colors[1] || {}
       const color3 = color_gradient?.colors[2] || {}
       return `
        background-image: linear-gradient(90deg, rgba(${color1.color} / ${color1.opacity}) 0%, rgba(${color2.color} / ${color2.opacity}) 50%, rgba(${color3.color} / ${color3.opacity}) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      `
     }
   }}
`
const SimpleText = Styled.div`
  ${body50}
  margin-block-start: 20px;
  p {
    margin-block-start: 0;
    margin-block-end: 0;
    &.isBold {
      font-weight: bold;
    }
  }
  
  `
const TextFilling = ({ paragraph, simple_paragraph, color_gradient, className, logo_image }) => {
  const console = useConsole()
  // console.verbose("TextFilling logo_image: ", logo_image)
  const { sources } = logo_image || {}
  const textRef = useRef(null)
  const coloredTextRef = useRef(null)
  const spanRefArray = useRef([])
  const spanRefArray2 = useRef([])
  const locale = useLocale()
  const ismobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  const isLanguageAr = locale.current.codes.ws === "ar" || locale.current.codes.ws === "fa"
  const isLanguageTh = locale.current.codes.ws === "th"
  const isLanguageRu = locale.current.codes.ws === "ru"
  const isSafari =
    typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("safari") && !navigator.userAgent.toLowerCase().includes("chrome")

  const spanrefNoGradientArray = useRef([])
  const spanrefNoGradientArray2 = useRef([])
  const isRetina = typeof window !== "undefined" && window.devicePixelRatio > 1

  const duration = 5
  useGSAP(() => {
    if (!coloredTextRef.current) return
    const timeout = setTimeout(() => {
      const tl2 = gsap.timeline({
        scrollTrigger: {
          trigger: coloredTextRef.current,
          start: "top center",
          end: "bottom center",
          scrub: true,
          markers: false,
        },
      })
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: coloredTextRef.current,
          start: "top center",
          end: "bottom center",
          scrub: true,
          markers: false,
        },
      })
      tl2.to(spanrefNoGradientArray.current, {
        opacity: 0.5,
        duration: duration,
        stagger: 1,
      })
      tl2.to(spanrefNoGradientArray2.current, {
        opacity: 0.5,
        duration: duration,
        stagger: 1,
      })
      tl.to(spanRefArray.current, {
        opacity: 1,
        duration: duration,
        stagger: 1,
      })
      tl.to(spanRefArray2.current, {
        opacity: 1,
        duration: duration,
        stagger: 1,
      })
    }, 500)
  }, [coloredTextRef, spanrefNoGradientArray, spanrefNoGradientArray2, spanRefArray, spanRefArray2])

  const paragraphParsed =
    paragraph?.block?.map(block => {
      // Parse the HTML string into React elements
      const parsed = parse(block.paragraph)

      // Helper to recursively walk the parsed tree and flatten to array of {letter, props, tag}
      function flattenToLetters(node, inheritedProps = {}, inheritedTag = null) {
        if (typeof node === "string") {
          // Split string into letters, each with inherited props and tag
          return node.split("").map(letter => ({
            letter,
            props: { ...inheritedProps },
            tag: inheritedTag,
          }))
        }
        if (React.isValidElement(node)) {
          // Merge props, but keep className and style
          const mergedProps = {
            ...inheritedProps,
            className: [inheritedProps.className, node.props.className].filter(Boolean).join(" ") || undefined,
            style: { ...inheritedProps.style, ...node.props.style },
          }
          // Pass tag name to children
          const tag = node.type
          return React.Children.toArray(node.props.children).flatMap(child => flattenToLetters(child, mergedProps, tag))
        }
        // If node is an array, flatten each child
        if (Array.isArray(node)) {
          return node.flatMap(child => flattenToLetters(child, inheritedProps, inheritedTag))
        }
        return []
      }

      return flattenToLetters(parsed)
    }) || []

  return (
    <Section className={className}>
      {sources.length > 0 && (
        <LogoWrapper>
          <ImageCld {...logo_image} sizes='10vw' />
        </LogoWrapper>
      )}
      <TextWrapper>
        <TextBase ref={textRef} isRu={isSafari && isLanguageRu}>
          {paragraph.block.map(block => (
            <p key={block._key}>{parse(block.paragraph)}</p>
          ))}
        </TextBase>
        {isLanguageAr || (isSafari && isLanguageTh) ? (
          <ColoredText aria-hidden='true' ref={coloredTextRef} color_gradient={color_gradient} className={isRetina ? "retina" : ""}>
            {paragraph.block.map(block => (
              <p key={block._key}>{parse(block.paragraph)}</p>
            ))}
          </ColoredText>
        ) : (
          <>
            <NoGradientText aria-hidden='true' ref={coloredTextRef} color_gradient={color_gradient} className={isRetina ? "retina" : ""}>
              {paragraphParsed.map((block, blockIndex) => (
                <p key={blockIndex}>
                  {block.map((item, index) => {
                    const { tag } = item

                    return (
                      <span
                        className='text-filling-letter'
                        key={`letter-${index}-${item.letter}`}
                        ref={
                          blockIndex === 0
                            ? element => (spanrefNoGradientArray.current[index] = element)
                            : element => (spanrefNoGradientArray2.current[index] = element)
                        }
                      >
                        {tag ? parse(`<${tag}>${item.letter}</${tag}>`) : item.letter}
                      </span>
                    )
                  })}
                </p>
              ))}
            </NoGradientText>
            <ColoredText aria-hidden='true' ref={coloredTextRef} color_gradient={color_gradient}>
              {paragraphParsed.map((block, blockIndex) => (
                <p key={blockIndex}>
                  {block.map((item, index) => {
                    const { tag } = item
                    return (
                      <span
                        className='text-filling-letter'
                        key={`letter-${index}-${item.letter}`}
                        ref={element => (blockIndex === 0 ? (spanRefArray.current[index] = element) : (spanRefArray2.current[index] = element))}
                      >
                        {tag ? parse(`<${tag}>${item.letter}</${tag}>`) : item.letter}
                      </span>
                    )
                  })}
                </p>
              ))}
            </ColoredText>
          </>
        )}
        <SimpleText>
          {simple_paragraph?.block.map(block => {
            console.log("block: ", block)
            const isBold = block.is_bold || false
            return (
              <p key={block._key} className={isBold ? "isBold" : ""}>
                {parse(block.paragraph)}
              </p>
            )
          })}
        </SimpleText>
      </TextWrapper>
    </Section>
  )
}

export default TextFilling
